import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'

import './workout-playground.sass'
import { CreateElement } from '@src/vue'
import { Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { WorkoutService } from '@src/service/workout'
import WorkoutData from '@src/data/workout-data'
import { LoadingScreen } from '@src/components/loading-screen/loading-screen/loading-screen'
import camelcaseKeys from 'camelcase-keys'
import { Icon } from '@src/elements/icon/icon'
import { WorkoutPlaygroundController } from '@src/routes/content/workout-playground/workout-playground-controller'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import {
  MobileLandscapeNotSupportedOverlay
} from '@src/components/mobile-landscape-not-supported-overlay/MobileLandscapeNotSupportedOverlay'

@Component({
  computed: {
    selectedWorkout () {
      return this.$store.state.new_workout
    }
  }
})
export class WorkoutPlayground extends Vue {
  private service: WorkoutService = new WorkoutService()
  private loading = true
  private startWorkout = false
  private apiClient: ApiClient

  private selectedWorkout
  private sidedSelectedWorkout
  private workoutSessionId

  private triggerVideosPlaying: boolean = false

  private intersectionObserver: IntersectionObserver

  async created () {
    this.apiClient = new ApiClient()
    return this.loadWorkout()
  }

  updated () {
    if (this.triggerVideosPlaying) {
      const videos: HTMLCollectionOf<HTMLVideoElement> = document.getElementsByClassName('video-preview') as HTMLCollectionOf<HTMLVideoElement>
      this.intersectionObserver = new IntersectionObserver(this.playVideoWhenInView, {
        root: null,
        rootMargin: '0px',
        threshold: 0.3
      })

      for (let i = 0; i < videos.length; i++) {
        const videoElement = videos.item(i) as HTMLVideoElement
        this.intersectionObserver.observe(videoElement)
      }

      this.triggerVideosPlaying = false
    }
  }

  playVideoWhenInView (entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.src = entry.target.getAttribute('data-src')
        entry.target.muted = true
        entry.target.play()
        try {
          entry.target.addEventListener('touchstart', (event) => {
            // Ensure touch event allows for scrolling
            event.stopPropagation()
          }, { passive: true })

          entry.target.addEventListener('touchmove', (event) => {
            // Ensure touch event allows for scrolling
            event.stopPropagation()
          }, { passive: true })

          entry.target.addEventListener('touchend', (event) => {
            // Ensure touch event allows for scrolling
            event.stopPropagation()
          }, { passive: true })
        } catch (e) {
          console.log('Error when trying to allow scrolling when touching video')
        }
      } else {
        entry.target.pause()
      }
    })
  }

  updatePreviewImage () {
    const { previewImage: defaultPreviewImage } = this.selectedWorkout

    const updatedPreviewImage = defaultPreviewImage.replace('/default-workout', '/default-workout-full-resolution')
    this.selectedWorkout.previewImage = updatedPreviewImage
  }

  updateSidedSelectedWorkout () {
    this.sidedSelectedWorkout = this.selectedWorkout
    const exerciseIds = Array.from(new Set(this.sidedSelectedWorkout.workoutExercises.map(workoutExercise => workoutExercise.exercise.id)))
    exerciseIds.forEach((exerciseId) => {
      let side = 0
      this.sidedSelectedWorkout.workoutExercises.forEach(workoutExercise => {
        if (workoutExercise.side === '' && workoutExercise.exercise.hasSides && workoutExercise.exercise.id === exerciseId) {
          workoutExercise.side = side % 2 === 0 ? 'left' : 'right'
          side++
        }
        if (workoutExercise.side === 'right') {
          side = 0
        }
        if (workoutExercise.side === 'left') {
          side = 1
        }
      })
    })
    /*this.sidedSelectedWorkout.workoutExercises.forEach((workoutExercise, index) => {
      if (workoutExercise.side === '' && workoutExercise.exercise.hasSides) {
        workoutExercise.side = index === 0 ? 'left' : this.sidedSelectedWorkout.workoutExercises[index - 1]!.side !== 'left' ? 'left' : 'right'
      }
    })*/
  }

  @Watch('selectedWorkout')
  watchSelectedWorkout () {
    this.updatePreviewImage()
    this.updateSidedSelectedWorkout()
  }

  @Watch('loading')
  watchPlayVideosWhenLoadingEnds () {
    this.triggerVideosPlaying = !this.loading
  }

  startHomeWorkoutFlow () {
    this.loading = true
    this.apiClient.post(API.MEMBERS_API.HOME_WORKOUT_START, {}, { 'workout_id': this.selectedWorkout.id }).then((response: any) => {
      this.workoutSessionId = response.workout_session.id
      this.startWorkout = true
      this.loading = false
    }).catch((error) => {
      console.log('error when starting home workout', { error })
    })
  }

  closePlayGround () {
    const baseLocation = window.location.pathname
    const destination = baseLocation.split('workout')[0]
    this.$router.replace(destination)
  }

  onRetrieveWorkoutSuccessful (response) {
    let exercises: any[] = response.workout.workout_exercises ? this.service.formatExercisesFromServer(response.workout.workout_exercises) : []
    let workoutToEdit = new WorkoutData(camelcaseKeys(response.workout, { deep: true }))
    this.$store.dispatch('setSelectedWorkout', { workout: workoutToEdit, exercises: exercises })
    this.loading = false
  }

  async loadWorkout () {
    if (this.$route.params.id) {
      let workoutId = parseInt(this.$route.params.id, 10)
      this.service.getWorkout(workoutId)
        .then(response => this.onRetrieveWorkoutSuccessful(response))
        .catch(async error => {
          console.error('failed to retrieve workout', error)
          this.loading = false
        })
    } else {
      this.loading = false
    }
  }

  render (h: CreateElement) {
    if (this.startWorkout && this.workoutSessionId) {
      return (
        <WorkoutPlaygroundController
          workout={this.sidedSelectedWorkout}
          workoutSessionId={this.workoutSessionId}
        />
      )
    }

    return (this.loading ? <LoadingScreen/> :
        <div class='workout-playground-wrapper'>
          <MobileLandscapeNotSupportedOverlay />
          <div class='playground-left'>
            <div class='preview-image'
              style={{ 'background-image': `url(${this.selectedWorkout.previewImage})` }}>
              <div class='back-wrapper'>
                <Icon name={'util/back-arrow'} click={this.closePlayGround}/>
                <div class='go-back' onClick={this.closePlayGround}><Subtitle1>{this.$t('BACK')}</Subtitle1></div>
              </div>
            </div>
              <div class='workout-info'>
                <div class='workout-name'><Subtitle1>{this.selectedWorkout.name}</Subtitle1></div>
                <div class='workout-info-items'>
                  <div class='workout-info-item'>
                    <Subtitle1>{this.$t('DURATION')}</Subtitle1>
                    <Subtitle2>{`${this.selectedWorkout.estimatedDurationInMin} ${this.$t('MINUTES')}`}</Subtitle2>
                  </div>
                  <div class='workout-info-item'>
                    <Subtitle1>{this.$t('PAGE.TRAINING_PLAN.FORM.WORKOUT.TYPE')}</Subtitle1>
                    <Subtitle2>{this.selectedWorkout.type}</Subtitle2>
                  </div>

                  <div class='start-workout' onClick={this.startHomeWorkoutFlow}>
                    {this.$t('PLAY_WORKOUT')}
                  </div>
                </div>
              </div>
          </div>

          <div class='playground-right scroll-content'>
            <div class='workout-specs'>
              <div class='workout-detail'>
                <Icon name='workout/exercises'/>
                <Subtitle1 class='detail-text'>{this.selectedWorkout.exerciseCount} {this.$t('EXERCISES')}</Subtitle1>
              </div>
              <div class='workout-detail'>
                <Icon name='workout/time'/>
                <Subtitle1 class='detail-text'>{this.selectedWorkout.estimatedDurationInMin} {this.$t('UNITS.MIN')}</Subtitle1>
              </div>
              <div class='workout-detail'>
                <Icon name='workout/score'/>
                <Subtitle1 class='detail-text'>{this.selectedWorkout.estimatedMetValue} {this.$t('UNITS.MET')}</Subtitle1>
              </div>
            </div>
            <div class={`workout-exercises-wrapper ${this.selectedWorkout.workoutExercises.length === 1 ? 'justify-center' : ''}`}>
              {this.sidedSelectedWorkout.workoutExercises.map((workoutExercise, index) => {
                const mp4Video = workoutExercise.exercise.videos.find(({ content_type }) => content_type === 'video/mp4')
                const videoUrl = mp4Video ? mp4Video.url : workoutExercise.exercise.videos[0].url

                return (
                  <div class='exercise-tile'>
                    <div class='video-wrapper'>
                      <video
                        class={`video-preview ${workoutExercise.side !== 'right' ? '' : 'mirrored'}`}
                        preload='metadata'
                        data-src={videoUrl}
                        disablePictureInPicture='true'
                        playsInline
                        autoPlay
                        loop
                      />
                    </div>
                    <div class='exercise-tile-info'>
                      <Subtitle2>{this.$te(`EXERCISE[${workoutExercise.exercise.number}].NAME`) ? this.$t(`EXERCISE[${workoutExercise.exercise.number}].NAME`) : workoutExercise.exercise.name}</Subtitle2>
                      <div class='tile-description'>
                        {
                          workoutExercise.equipmentWithWeight.map(({ name, weight, weightUnit }) => {
                            return (
                              <div className='repetitions-wrapper'>
                                <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + name.toUpperCase())}</Subtitle1>
                                {weight && weightUnit ? <Subtitle2>{`${weight} ${weightUnit}`}</Subtitle2> : ''}
                              </div>
                            )
                          }
                          )
                        }
                        <div class='repetitions-wrapper'>
                          <Subtitle1>Repetitions</Subtitle1>
                          <Subtitle2>{workoutExercise.initialExerciseConfig.repetitions}</Subtitle2>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
              }
            </div>
          </div>
        </div>
    )
  }
}
